import { gql, useMutation } from "@apollo/client";
import { useEffect } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import MaintenanceFullPage from "components/MaintenanceFullPage";
import SpaceliftLoader from "components/loading/SpaceliftLoader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useMaintenanceStore from "shared/Maintenance/store";
import setLocation from "shared/setLocation";
import { OAuthRedirect } from "types/generated";

import useInstanceProviderGateRules from "./SelfHosted/InstanceProvider/useInstanceProviderGateRules";

/** @public (Knip ignored - used in the file and exported for testing) */
export const LOGIN = gql`
  mutation OAuthLogin {
    oauthRedirect {
      providerUrl
      temporaryToken
      enabled
    }
  }
`;

type LoginGql = {
  oauthRedirect: OAuthRedirect;
};

const Login = () => {
  useTitle("Redirecting to OAuth provider...");

  const { onError, reportError } = useTypedContext(FlashContext);
  const [redirect, { error }] = useMutation<LoginGql>(LOGIN);
  const ErrorContent = useErrorHandle(error);
  const { isSelfHostedInstalled, isAdminLoginEnabled } = useInstanceProviderGateRules();
  const isMaintenanceActive = useMaintenanceStore((state) => state.enabled);

  useEffect(() => {
    if (isMaintenanceActive) return;

    const asyncEffect = async () => {
      try {
        const { data } = await redirect();

        const ssoNotEnabledYetForSH = isSelfHostedInstalled && !data?.oauthRedirect.enabled;

        const adminLoginDisabled = ssoNotEnabledYetForSH && !isAdminLoginEnabled;
        if (adminLoginDisabled) {
          reportError({ title: "Something went wrong!", message: "Admin login is disabled" });
          return;
        }
        if (ssoNotEnabledYetForSH) {
          setLocation("/admin-login");
          return;
        }

        if (data) {
          localStorage.setItem("temporaryToken", data.oauthRedirect.temporaryToken);
          setLocation(data.oauthRedirect.providerUrl);
        }
      } catch (e) {
        onError(e);
      }
    };

    asyncEffect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaintenanceActive]);

  if (isMaintenanceActive) {
    return <MaintenanceFullPage />;
  }

  if (ErrorContent) {
    return ErrorContent;
  }

  return <SpaceliftLoader />;
};

export default Login;
